import React, { useEffect, useState } from 'react';

import { Button } from '@eika/button';
import { ErrorCard } from '@eika/card';
import { Loader } from '@eika/loader';
import { RadioButtons } from '@eika/radio-button';

import { useCountryDetails } from '../../../../fetchHooks/useCountryDetails';
import type { CountryCode } from '../../../../generated-types';
import useTranslation from '../../../../hooks/useTranslation';
import { ErrorCardWrapper } from '../../../shared/ErrorCardWrapper';
import { CrossBorderPaymentActionType } from '../form/actions';
import useFormContext from '../form/useFormContext';
import { receiverError } from '../form/validate';
import BankValidationFields from './BankValidationFields';
import IbanInput from './IbanInput';
import NewCrossBorderBankDetails from './NewCrossBorderBankDetails';
import NewCrossBorderReceiverCreditorDetails from './NewCrossBorderReceiverCreditorDetails';

interface Props {
    countryCode?: CountryCode;
    validationErrors?: receiverError;
    setValidationErrors: (errors: receiverError) => void;
    setShowNextButton: (value: boolean) => void;
}

const NewCrossBorderReceiverBank: React.FC<Props> = ({
    countryCode,
    validationErrors,
    setValidationErrors,
    setShowNextButton,
}) => {
    const translated = useTranslation();
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
        translated.crossBorderPayment.newReceiver.ibanLabel,
    );
    const { state, handleChange } = useFormContext();
    const {
        data: countryDetails,
        error: detailsError,
        isLoading: loadingDetails,
        refetch: refetchDetails,
    } = useCountryDetails(countryCode);

    useEffect(() => {
        if (!state.receiver?.bankCountryCode) {
            handleChange({
                type: CrossBorderPaymentActionType.SET_COUNTRY_INFO,
                payload: undefined,
            });
            return;
        }
        if (countryDetails && !state.country) {
            handleChange({
                type: CrossBorderPaymentActionType.SET_COUNTRY_INFO,
                payload: countryDetails,
            });
        }
    }, [countryDetails, handleChange, state.country, state.receiver?.bankCountryCode]);

    if (loadingDetails) {
        return <Loader mini />;
    }

    if (detailsError) {
        return (
            <ErrorCardWrapper
                className="country-details-error max-desktop-width"
                exception={detailsError}
                defaultError={translated.crossBorderPayment.newReceiver.errorCountryDetails}
            >
                <Button onClick={() => refetchDetails()}>{translated.tryAgain}</Button>
            </ErrorCardWrapper>
        );
    }

    if (state.country && !state.country?.validForPayments) {
        return (
            <ErrorCard
                className="country-details-error max-desktop-width"
                title={translated.crossBorderPayment.newReceiver.errorCountryTitle}
                description={translated.crossBorderPayment.newReceiver.validForPaymentError}
            />
        );
    }

    return (
        <>
            {state.country?.ibanRequirements === 'MANDATORY' && (
                <IbanInput validationError={validationErrors} setValidationError={setValidationErrors} />
            )}
            {state.country?.ibanRequirements === 'OPTIONAL' && (
                <>
                    <RadioButtons
                        label={translated.crossBorderPayment.newReceiver.paymentOptions}
                        className="new-cross-border-receiver__payment-option"
                        onChange={setSelectedPaymentMethod}
                        options={[
                            { value: translated.crossBorderPayment.newReceiver.ibanLabel },
                            { value: translated.accountNumber },
                        ]}
                        selectedOption={selectedPaymentMethod}
                    />
                    {selectedPaymentMethod === translated.accountNumber ? (
                        <BankValidationFields
                            validationError={validationErrors}
                            setValidationError={setValidationErrors}
                        />
                    ) : (
                        <IbanInput validationError={validationErrors} setValidationError={setValidationErrors} />
                    )}
                </>
            )}
            {state.country?.ibanRequirements === 'NOT_IN_USE' && (
                <BankValidationFields validationError={validationErrors} setValidationError={setValidationErrors} />
            )}
            {state.receiver?.accountType && (
                <NewCrossBorderBankDetails
                    validationErrors={validationErrors}
                    setValidationErrors={setValidationErrors}
                    setShowNextButton={setShowNextButton}
                />
            )}
            {state.bankValidation?.valid && (
                <NewCrossBorderReceiverCreditorDetails
                    setValidationErrors={setValidationErrors}
                    validationErrors={validationErrors}
                />
            )}
        </>
    );
};

export default NewCrossBorderReceiverBank;
